import { i18n } from "@/plugins/i18n";
import {
  getCurrentDate,
  getFirstDayThisYear,
  getNDaysAgo,
  getNMonthsAgo,
} from "@/constants/date-range-picker";
import {
  SubscriptionModule,
  type TicketGeneratingSubscriptionModule,
} from "@/constants/workplaces.ts";
import { CoroIcons } from "@/constants/coro-icon";

/** How long tickets stay in the live database before moving to the archive. */
const REPORTS_LIVE_WINDOW_DAYS = 90;

type ReportDateRangePresetOption = { label: string; value: [string, string] };

/** Declared as day counts, so the window filter below can ask how far back each range reaches. */
const REPORT_PRESET_DAYS: Array<{ days: number; labelKey: string }> = [
  { days: 7, labelKey: "dateRangePicker.lastSevenDays" },
  { days: 30, labelKey: "dateRangePicker.lastThirtyDays" },
  { days: REPORTS_LIVE_WINDOW_DAYS, labelKey: "dateRangePicker.lastNinetyDays" },
  { days: 365, labelKey: "dateRangePicker.last365Days" },
];

type ReportPreset = (typeof REPORT_PRESET_DAYS)[number];

/** Both ends are included, so "Last 30 days" runs D-29..D — thirty days, as the label says. */
const toDateRangePreset = ({ days, labelKey }: ReportPreset): ReportDateRangePresetOption => ({
  label: i18n.global.t(labelKey),
  value: [getNDaysAgo(days - 1), getCurrentDate()],
});

/**
 * Earliest day the capped reports offer. Inclusive counting also keeps it inside
 * `WarningEmailEventsReportParams.isValidDateRange`, the backend rule that measures the window in
 * UTC against a date the front end builds from the browser's clock.
 */
export const getReportsLiveWindowFloor = (): string => getNDaysAgo(REPORTS_LIVE_WINDOW_DAYS - 1);

export const getReportsDefaultDateRangePresets = (): ReportDateRangePresetOption[] =>
  REPORT_PRESET_DAYS.map(toDateRangePreset);

/** The ranges that stay inside the live window — a report bounded to it cannot serve the rest. */
export const getNinetyDayReportsDateRangePresets = (): ReportDateRangePresetOption[] =>
  REPORT_PRESET_DAYS.filter(({ days }) => days <= REPORTS_LIVE_WINDOW_DAYS).map(toDateRangePreset);

// Security awareness training reports (security training & phishing simulation summaries)
// use month-based ranges instead of the day-based defaults
export const getSatReportsDateRangePresets = (): Array<{
  label: string;
  value: [string, string];
}> => [
  {
    label: i18n.global.t("dateRangePicker.lastThirtyDays"),
    value: [getNDaysAgo(30), getCurrentDate()],
  },
  {
    label: i18n.global.t("dateRangePicker.lastThreeMonths"),
    value: [getNMonthsAgo(3), getCurrentDate()],
  },
  {
    label: i18n.global.t("dateRangePicker.lastSixMonths"),
    value: [getNMonthsAgo(6), getCurrentDate()],
  },
  {
    label: i18n.global.t("dateRangePicker.thisYear"),
    value: [getFirstDayThisYear(), getCurrentDate()],
  },
];

export enum ReportType {
  EXECUTIVE_SUMMARY = "workspace",
  MANAGED_SERVICES_SUMMARY = "soc",
  DNS_SUMMARY = "dns",
  SECURED_MESSAGES = "securedMessages",
  PHISHING_SIMULATION = "phishingSimulation",
  SECURITY_AWARENESS = "securityAwareness",
  WARNING_ONLY_EMAIL_EVENTS = "warningEmailEvents",
  WARNING_ONLY_EMAIL_EVENTS_MSP = "warningEmailEventsMsp",
}

// MSP Warning-Only Email Events report: BE limits returned workspaces, default and max value is 2500
export const MSP_WARNING_EMAIL_EVENTS_WORKSPACE_LIMIT = 2500;

export const DNS_REPORT_DEFAULT_PAGE_SIZE = 25;

// SAT reports: the user list itself shows the top 25 users, while searching queries all enrolled
// users on the BE (capped at 100 matches per search).
export const REPORT_USERS_SEARCH_MIN_LENGTH = 2;

export const REPORT_USERS_SEARCH_DEBOUNCE = 300;

export enum ReportDateRangePreset {
  LAST_7_DAYS = "last_7_days",
  LAST_30_DAYS = "last_30_days",
  LAST_90_DAYS = "last_90_days",
  LAST_365_DAYS = "last_365_days",
}

export enum DnsDomainStatus {
  ALLOWED = "Allowed",
  BLOCKED = "Blocked",
}

export enum ReportTilesVariant {
  INDIGO = "indigo",
  TEAL = "teal",
  VIOLET = "violet",
}

export enum ReportMetricValueFormat {
  NUMBER = "number",
  PERCENT = "percent",
}

export enum ExecutiveSummaryEmailListType {
  ALLOWLIST = "allowlist",
  BLOCKLIST = "blocklist",
}

// Today's order, lifted out of ExecutiveSummaryReportsTab.vue's local `sortOrder`.
// Drives both the impact grid and the protection sections.
export const EXECUTIVE_SUMMARY_MODULE_ORDER: TicketGeneratingSubscriptionModule[] = [
  SubscriptionModule.CLOUD_SECURITY,
  SubscriptionModule.EMAIL_SECURITY,
  SubscriptionModule.ENDPOINT_SECURITY,
  SubscriptionModule.USER_DATA_GOVERNANCE,
  SubscriptionModule.ENDPOINT_DATA_GOVERNANCE,
  SubscriptionModule.EDR,
];

/**
 * Set on the report root for the duration of a PDF export.
 *
 * html2pdf lays the report out in a container exactly one page wide, and the snapshot it takes
 * of each chart keeps whatever aspect ratio it was captured at — so a chart captured at the
 * browser's width lands in the document at the wrong height. A report that cares pins itself to
 * the page width in its own stylesheet while this class is present; one that defines no rule for
 * it is unaffected, which is why the export sets it unconditionally.
 */
export const REPORT_PDF_EXPORT_CLASS = "pdf-export";

/** The modules any report renders a heading for: the six ticket-generating ones plus SAT. */
export type ReportModule =
  TicketGeneratingSubscriptionModule | SubscriptionModule.SECURITY_AWARENESS_TRAINING;

/**
 * Categorical palette for report charts, in slice order.
 * The theme's "label" family rather than {@link DoughnutChartColors}, whose SUCCESS green and
 * ERROR red read as pass/fail on a breakdown that carries no such meaning. Twelve entries because
 * the palette cycles by index and a doughnut's last slice sits against its first.
 */
export enum ReportSliceColorToken {
  PURPLE = "labels-purple-full",
  BLUE = "labels-blue-full",
  ORANGE = "labels-orange-full",
  GREEN = "labels-green-full",
  YELLOW = "labels-yellow-full",
  GREY = "indigo-medium",
  RED = "labels-red-full",
  PURPLE_LIGHT = "labels-purple-light",
  BLUE_LIGHT = "labels-blue-light",
  ORANGE_LIGHT = "labels-orange-light",
  GREEN_LIGHT = "labels-green-light",
  YELLOW_LIGHT = "labels-yellow-light",
}

export const REPORT_SLICE_COLOR_ORDER: ReportSliceColorToken[] = [
  ReportSliceColorToken.PURPLE,
  ReportSliceColorToken.BLUE,
  ReportSliceColorToken.ORANGE,
  ReportSliceColorToken.GREEN,
  ReportSliceColorToken.YELLOW,
  ReportSliceColorToken.GREY,
  ReportSliceColorToken.RED,
  ReportSliceColorToken.PURPLE_LIGHT,
  ReportSliceColorToken.BLUE_LIGHT,
  ReportSliceColorToken.ORANGE_LIGHT,
  ReportSliceColorToken.GREEN_LIGHT,
  ReportSliceColorToken.YELLOW_LIGHT,
];

/**
 * The plain 24px glyph each module is drawn with, for a heading that sits inline with its text:
 * every Executive Summary surface. The `-s` artwork the mock specifies, not the framed badge in
 * {@link REPORT_MODULE_ICONS_L} scaled down — that one keeps its hexagon frame at any size, so
 * at 24px the frame is most of what is left.
 *
 * Exhaustive over {@link ReportModule}, so adding a module to that union is a compile error here
 * until an icon is supplied — the per-component copies this replaced were
 * `Record<string, string>`, whose index signature promised an icon for any key and silently
 * returned `undefined`.
 */
export const REPORT_MODULE_ICONS_S: Record<ReportModule, string> = {
  [SubscriptionModule.CLOUD_SECURITY]: `$${CoroIcons.CLOUD_APP_S}`,
  [SubscriptionModule.EMAIL_SECURITY]: `$${CoroIcons.EMAIL_S}`,
  [SubscriptionModule.ENDPOINT_SECURITY]: `$${CoroIcons.DEVICE_S}`,
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: `$${CoroIcons.ENDPOINT_DATA_GOVERNANCE_S}`,
  [SubscriptionModule.USER_DATA_GOVERNANCE]: `$${CoroIcons.DATA_S}`,
  [SubscriptionModule.EDR]: `$${CoroIcons.EDR_S}`,
  [SubscriptionModule.SECURITY_AWARENESS_TRAINING]: `$${CoroIcons.SECURITY_AWARENESS_S}`,
};

/**
 * The framed 40px badge each module is drawn with, for the Managed Services Summary's module
 * overviews, whose heading stands above its text rather than beside it.
 *
 * The SAT entry is the small glyph rather than a badge, inherited from when both reports shared
 * one map. It is NOT unreachable, contrary to what this comment used to claim:
 * `ManagedServicesResolvedTickets` is a mapped type over every `SubscriptionModule`, and
 * `ManagedServicesSummaryTab` iterates the payload with `Object.entries` and only SORTS it — an
 * unknown module gets `indexOf === -1` and is placed FIRST. So a payload carrying SAT would print
 * a 24px `-s` glyph at `size="40"` above Cloud Security. Left as-is deliberately: giving it the
 * `-l` badge changes a second report's output, which belongs in its own change.
 */
export const REPORT_MODULE_ICONS_L: Record<ReportModule, string> = {
  [SubscriptionModule.CLOUD_SECURITY]: `$${CoroIcons.CLOUD_SECURITY}`,
  [SubscriptionModule.EMAIL_SECURITY]: `$${CoroIcons.EMAILS}`,
  [SubscriptionModule.ENDPOINT_SECURITY]: `$${CoroIcons.ENDPOINT_SECURITY}`,
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: `$${CoroIcons.ENDPOINT_DATA_GOVERNANCE}`,
  [SubscriptionModule.USER_DATA_GOVERNANCE]: `$${CoroIcons.USER_DATA_GOVERNANCE}`,
  [SubscriptionModule.EDR]: `$${CoroIcons.EDR}`,
  [SubscriptionModule.SECURITY_AWARENESS_TRAINING]: `$${CoroIcons.SECURITY_AWARENESS_S}`,
};
